import React, { useState, useCallback, ComponentType } from 'react'
import {
  Icon,
  Image,
  Button,
  withApplicationTheme,
  ProductLine,
  constants,
} from '@adc/ui-core'
import classNames from 'classnames/bind'
import configment from '@adc/configment'
import type { primitives as BlueprintPrimitivesTypes } from '@adc/blueprint'
import NavigationDrawer from '../navigationDropdown/NavigationDrawer'
import {
  getNavigationMenuItems,
  isDealsForYouNavEnabled,
} from '../navigationDropdown/constants'
import { NavigationMenuItem } from '../navigationDropdown/types'
import auctioncomIconLogo from '../../../img/auction-logo-mobile.svg'
import auctioncomFullLogo from '../../../img/logo-light.svg'
import smartsaleIconLogo from '../../../img/smartsale-logo.svg'
import { resolveUrl } from '../../../util/url'
import styles from './styles.scss'

const cx = classNames.bind(styles)

interface Theme {
  productLine: typeof ProductLine.AuctionCom | typeof ProductLine.SmartSale
  version?: 1
}

interface MiddleContentProps {
  children?: React.ReactNode
  showLoginButton: boolean
  onLoginClick: (e: React.MouseEvent) => void
  elmId: string
}

const MiddleContent: React.FC<MiddleContentProps> = ({
  children,
  showLoginButton,
  onLoginClick,
  elmId,
}) => {
  const loginButton = showLoginButton && (
    <Button
      role='secondary'
      className={cx('login-button')}
      onClick={onLoginClick}
      elmId={`${elmId}_login_button`}
    >
      Log In
    </Button>
  )

  if (typeof children === 'string') {
    return (
      <>
        <span className={cx('screen-title')}>{children}</span>
        {loginButton}
      </>
    )
  }

  return (
    <>
      {children}
      {loginButton}
    </>
  )
}

export interface NewMobileHeaderProps {
  logoVariant?: 'icon' | 'full'
  children?: React.ReactNode
  showMobileLoginButton?: boolean
  user?:
    | BlueprintPrimitivesTypes.AuthenticatedAuctionUser
    | { logged_in?: boolean; first_name?: string }
  featureFlags?: Record<string, unknown>
  onLoginClick?: (e: React.MouseEvent) => void
  onSignupClick?: (e: React.MouseEvent) => void
  onLogoutClick?: (e: React.MouseEvent) => void
  onClickBell?: (e: React.MouseEvent) => void
  bellNotificationsCount?: string | number
  theme: Theme
  forceSmartSaleLogo?: boolean
  elmId?: string

  hideShadow?: boolean
  showDealsForYouIndicator?: boolean
  dealsFresh?: boolean
  // Origin to prefix root-relative links with (e.g. "https://www.auction.com").
  baseUrl?: string
}

const NewMobileHeader: React.FC<NewMobileHeaderProps> = ({
  logoVariant = 'icon',
  children,
  showMobileLoginButton = false,
  user = { logged_in: false },
  featureFlags = {},
  onLoginClick,
  onSignupClick,
  onLogoutClick,
  onClickBell,
  bellNotificationsCount,
  theme,
  forceSmartSaleLogo = false,
  elmId = 'new_mobile_header',
  hideShadow = false,
  showDealsForYouIndicator = false,
  dealsFresh,
  baseUrl = '',
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleMenuToggle = useCallback(() => {
    setIsMenuOpen(prev => !prev)
  }, [])

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false)
  }, [])

  const handleLoginClick = useCallback(
    (e: React.MouseEvent) => {
      handleMenuClose()
      onLoginClick?.(e)
    },
    [handleMenuClose, onLoginClick]
  )

  const handleSignupClick = useCallback(
    (e: React.MouseEvent) => {
      handleMenuClose()
      onSignupClick?.(e)
    },
    [handleMenuClose, onSignupClick]
  )

  const isSmartSale =
    theme.productLine === ProductLine.SmartSale || forceSmartSaleLogo
  let logoSrc: string
  let logoIconFallbackSrc: string | null = null

  if (isSmartSale) {
    const smartsalePoweredLogo = configment.get(
      'SMARTSALE_POWERED_LOGO_URL',
      ''
    )
    logoSrc = logoVariant === 'full' ? smartsalePoweredLogo : smartsaleIconLogo
    if (logoVariant === 'full') logoIconFallbackSrc = smartsaleIconLogo
  } else {
    logoSrc = logoVariant === 'full' ? auctioncomFullLogo : auctioncomIconLogo
    if (logoVariant === 'full') logoIconFallbackSrc = auctioncomIconLogo
  }

  const menuItems: NavigationMenuItem[] = getNavigationMenuItems(
    featureFlags,
    {
      onSignOut: onLogoutClick,
      onNotifications: onClickBell,
      onLogin: handleLoginClick,
      onSignUp: handleSignupClick,
    },
    user?.logged_in ?? false,
    theme,
    {
      user,
      isMobile: true,
      showDealsForYouIndicator,
      dealsFresh,
      baseUrl,
    }
  )

  let formattedNotificationCount = ''
  if (user?.logged_in && bellNotificationsCount) {
    const count =
      typeof bellNotificationsCount === 'string'
        ? parseInt(bellNotificationsCount, 10)
        : bellNotificationsCount

    if (!isNaN(count) && count > 0) {
      formattedNotificationCount = count >= 20 ? '20+' : String(count)
    }
  }

  // Aggregate orange dot on the hamburger (mobile equivalent of the desktop
  // account-button dot): shown when the menu has any unseen item. Static.
  const dealsIndicatorsEnabled = isDealsForYouNavEnabled(featureFlags)
  const showHamburgerIndicator =
    dealsIndicatorsEnabled &&
    (user?.logged_in ?? false) &&
    (showDealsForYouIndicator || !!formattedNotificationCount)

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault()
    window.location.href = resolveUrl('/', baseUrl)
  }

  const logoClassName = (() => {
    if (logoVariant === 'full') {
      return isSmartSale ? 'logo-full-smartsale' : 'logo-full'
    }
    return isSmartSale ? 'logo-icon-smartsale' : 'logo-icon'
  })()

  const logoElement = (
    <a
      href={resolveUrl('/', baseUrl)}
      onClick={handleLogoClick}
      className={cx('logo-link', 'back-button')}
      data-elm-id={`${elmId}_logo`}
    >
      <Image
        src={logoSrc}
        alt='Auction.com'
        className={cx('logo', logoClassName)}
      />
      {logoIconFallbackSrc && (
        <Image
          src={logoIconFallbackSrc}
          alt='Auction.com'
          className={cx('logo', 'logo-icon', 'logo-icon-fallback')}
        />
      )}
    </a>
  )

  const showLoginButton = showMobileLoginButton && !user?.logged_in
  const hasMiddleContent = children || showLoginButton

  return (
    <div
      className={cx('new-mobile-header', { 'hide-shadow': hideShadow })}
      data-elm-id={elmId}
    >
      <div className={cx('header-container')}>
        <div className={cx('header-left')}>{logoElement}</div>

        {hasMiddleContent && (
          <div className={cx('header-middle')}>
            <MiddleContent
              showLoginButton={showLoginButton}
              onLoginClick={handleLoginClick}
              elmId={elmId}
            >
              {children}
            </MiddleContent>
          </div>
        )}

        <div className={cx('header-right')}>
          <Button
            className={cx('hamburger-button')}
            onClick={handleMenuToggle}
            data-elm-id={`${elmId}_hamburger`}
            aria-label='Open menu'
            aria-expanded={isMenuOpen}
          >
            <span className={cx('hamburger-icon-wrap')}>
              <Icon
                name='bars'
                iconStyle='solid'
                color={constants.ColorNames.Gray}
                size='large'
              />
              {showHamburgerIndicator && (
                <span
                  className={cx('hamburger-indicator-dot')}
                  data-elm-id={`${elmId}_indicator`}
                />
              )}
            </span>
          </Button>
        </div>
      </div>

      <NavigationDrawer
        user={user}
        menuItems={menuItems}
        formattedNotificationCount={formattedNotificationCount}
        isOpen={isMenuOpen}
        onClose={handleMenuClose}
        elmId={`${elmId}_drawer`}
        indicatorsEnabled={dealsIndicatorsEnabled}
      />
    </div>
  )
}

const NewMobileHeaderThemed = withApplicationTheme(
  NewMobileHeader as ComponentType<NewMobileHeaderProps & { theme: Theme }>
)

export { NewMobileHeaderThemed as NewMobileHeader }

// Export unwrapped component for testing
export { NewMobileHeader as NewMobileHeaderBase }
